/**
 * BS-2151: client-side sanitization for form-level analytics.
 * BS-2169: tightened filterMetadata value-type enforcement.
 *
 * # PII boundary
 *
 * OLA is member-facing and handles SSN, DOB, names, addresses, account
 * numbers, income — all raw values that NCUA-regulated data handling
 * forbids from leaving the browser uncontrolled. This module is the
 * single chokepoint that converts a raw form value into the sanitized
 * characteristics shape the BS-2150 API will accept.
 *
 * The rules:
 *
 *   1. Raw values NEVER leave this module. Callers pass a value in;
 *      callers get back ONLY the characteristic counts. There is no
 *      escape hatch. There is no "include first 4 chars" debug mode.
 *      There is no hash. The contract is value -> shape-of-value.
 *
 *   2. The shape we emit exactly mirrors the canonical
 *      `errorMetadataSchema` in `@multiplicu/contracts/los`:
 *      `length`, `has_special_chars`, `has_numbers`, `has_letters`.
 *      Adding a new metadata key here REQUIRES a parallel update to
 *      the server schema, because the server uses `.strict()` and will
 *      reject any extra key — that rejection IS the contract.
 *
 *   3. Non-string values yield a minimal shape (no length, no has_*).
 *      Numbers, booleans, dates, etc. don't carry the kinds of PII
 *      this scrub is defending against, and surfacing a `length` for
 *      a number would be misleading.
 *
 * # Why this is independent of the Sentry PII scrubber
 *
 * The Sentry scrubber (`../sentry/pii-scrubber.ts`) is a field-name
 * deny-list: walk the event object, redact values under PII-shaped
 * keys. That's appropriate for error telemetry where the payload shape
 * is unpredictable.
 *
 * Here, the shape is fixed by the BS-2150 schema and the threat model
 * is different: instead of "an error payload might accidentally
 * contain a raw value," it's "the analytics pipeline must NEVER
 * accidentally contain a raw value." So the design is: don't carry
 * raw values past this module, ever. Sanitize at the emit site, hand
 * the emit module only the sanitized shape, and the emit module has
 * no way to leak what it never received.
 */

import type { ErrorMetadata } from '@multiplicu/contracts/los';

/**
 * Compute the sanitized error_metadata shape for a raw form value.
 *
 * Returns `undefined` (not an empty object) for nullish / non-string
 * values, so the caller can omit the field from the emitted event
 * entirely rather than emit `error_metadata: {}` which is noise.
 *
 * Strings are reduced to four booleans + one count. The raw value
 * never escapes this function — it is read once for the characteristic
 * computation and discarded. Callers should not pass references that
 * keep the raw value alive beyond their needs; the typical pattern is
 * to derive metadata at the validation-error emit site and never store
 * the raw value in any analytics-bound buffer.
 */
export function sanitizeFieldValue(value: unknown): ErrorMetadata | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== 'string') return undefined;

  return {
    length: value.length,
    has_special_chars: /[^a-zA-Z0-9\s]/.test(value),
    has_numbers: /[0-9]/.test(value),
    has_letters: /[a-zA-Z]/.test(value),
  };
}

/**
 * Canonical type validators for each allow-listed metadata key.
 * Mirrors the `errorMetadataSchema` in `@multiplicu/contracts/los`:
 *   - `length` must be a finite number (BS-2150 rejects strings like
 *     "123-45-6789" via `.strict()`, and also rejects NaN/Infinity via
 *     the `z.number()` schema which calls `Number.isFinite` internally)
 *   - `has_*` fields must be booleans
 *
 * BS-2169: enforcing types here makes the client-side defense load-
 * bearing, not just a key-name filter. A caller that sneaks a raw SSN
 * string through an allow-listed key (e.g. `{ length: "123-45-6789" }`)
 * is rejected here rather than relying solely on the server's `.strict()`
 * Zod schema as the backstop.
 *
 * BS-2173: `length` validator tightened from `typeof v === 'number'` to
 * `Number.isFinite(v)`. The server schema uses `z.number()` which also
 * rejects NaN and Infinity - `typeof NaN === 'number'` is true but
 * `Number.isFinite(NaN)` is false, so the old guard would have let NaN
 * and Infinity through the client-side filter while the server would
 * reject them. Aligning here means a round-trip reject on the server
 * cannot happen for a value the client-side filter already accepted.
 */
type MetadataTypeValidator = (value: unknown) => boolean;

const ALLOWED_METADATA_VALIDATORS: ReadonlyMap<keyof ErrorMetadata, MetadataTypeValidator> =
  new Map<keyof ErrorMetadata, MetadataTypeValidator>([
    ['length', (v) => Number.isFinite(v)],
    ['has_special_chars', (v) => typeof v === 'boolean'],
    ['has_numbers', (v) => typeof v === 'boolean'],
    ['has_letters', (v) => typeof v === 'boolean'],
  ]);

/**
 * Strip any keys outside the allow-list OR with an incorrect value
 * type from a metadata object. Returns `undefined` if the result
 * would be empty so callers can omit the field from the event.
 *
 * Two-layer defense (BS-2169):
 *   1. Key allow-list — drops any key not in the canonical schema.
 *   2. Value-type check — drops any allow-listed key whose value does
 *      not match the canonical type (number for `length`, boolean for
 *      `has_*`). This prevents callers from smuggling a raw string
 *      (e.g. an SSN) through an allow-listed key.
 *
 * The server's `.strict()` schema is still the load-bearing contract,
 * but this filter makes the client-side defense real rather than
 * just a key-name guard.
 */
export function filterMetadata(
  metadata: Record<string, unknown> | undefined
): ErrorMetadata | undefined {
  if (!metadata) return undefined;
  const filtered: Record<string, unknown> = {};
  let kept = 0;
  for (const key of Object.keys(metadata)) {
    const validator = ALLOWED_METADATA_VALIDATORS.get(key as keyof ErrorMetadata);
    if (validator && validator(metadata[key])) {
      filtered[key] = metadata[key];
      kept += 1;
    }
  }
  return kept > 0 ? (filtered as ErrorMetadata) : undefined;
}
